import { Locale, format } from 'date-fns'
// date-fns' own defaultLocale statically bundles en-US wherever `format` goes, so a dynamic
// import gains nothing and only triggers Rollup's dynamic+static conflict warning.
import { enUS } from 'date-fns/locale/en-US'

type Loader = () => Promise<Locale>

const loaders: Record<string, Loader> = {
	en: () => Promise.resolve(enUS as Locale),
	fr: () => import('date-fns/locale/fr').then((m) => m.fr as Locale),
	nl: () => import('date-fns/locale/nl').then((m) => m.nl as Locale),
	de: () => import('date-fns/locale/de').then((m) => m.de as Locale),
}

let currentLocale: Locale | undefined
let currentLanguage: string | undefined

// Synchronous accessor for the already-loaded locale (undefined until first load resolves).
export const getActiveDateFnsLocale = (): Locale | undefined => currentLocale

export const loadDateFnsLocale = async (language: string): Promise<Locale | undefined> => {
	// Normalize language to our supported keys
	const lang = loaders[language] ? language : 'nl'
	if (lang === currentLanguage && currentLocale) {
		return currentLocale
	}

	try {
		currentLocale = await loaders[lang]()
		currentLanguage = lang
	} catch {
		// Silent fallback to Dutch if a dynamic import fails (e.g. unexpected lang code)
		if (lang !== 'nl') {
			currentLocale = await loaders.nl()
			currentLanguage = 'nl'
		}
	}

	return currentLocale
}

export const ensureDateFnsLocale = (language: string) => loadDateFnsLocale(language)

export const formatDateWithActiveLocale = (date: Date, withTime = true) => {
	// If locale not yet loaded, date-fns falls back to default (English) formatting.
	const formatString = withTime ? 'dd MMMM yyyy - HH:mm' : 'dd MMMM yyyy'
	return format(date, formatString, { locale: currentLocale })
}
